import type { ImageModelOption } from "@/types/api";
import type {
  DefaultStyleResponse,
  DefaultStyleUpdateBody,
  ProjectDetailsResponse,
  StyleDnaGeneration,
  StyleDnaPanelDraftPrefillPayload,
  StyleDnaPanelPreviewMode,
  StyleDnaPendingGeneration
} from "@/types/project";

export function assignedStyleDnaIdFromProject(projectDetails: ProjectDetailsResponse | null, projectId: string | null): string | null {
  if (typeof projectId !== "string" || projectId.length === 0 || projectDetails === null || projectDetails.project_id !== projectId) {
    return null;
  }

  const id = projectDetails.project["style-dna-id"];
  if (id === null || id === undefined) {
    return null;
  }

  if (typeof id === "string") {
    const t = id.trim();
    return t.length > 0 ? t : null;
  }

  const s = String(id).trim();
  return s.length > 0 ? s : null;
}

export function projectHasAssignedStyleDna(projectDetails: ProjectDetailsResponse | null, projectId: string | null): boolean {
  return assignedStyleDnaIdFromProject(projectDetails, projectId) !== null;
}

export function defaultStyleUpdateBody(style: DefaultStyleResponse): DefaultStyleUpdateBody {
  const { lock: _lock, ...rest } = style;
  return rest;
}

export type StyleDnaDocumentKey = "general" | "character" | "environment";

export type StyleDnaTriple = Readonly<{
  general: string;
  character: string;
  environment: string;
}>;

type StyleDnaFieldSource = Readonly<{
  general_style_dna?: string;
  character_style_dna?: string;
  environment_style_dna?: string;
}>;

export type StyleDnaTripleSnapshot = StyleDnaTriple;

export const EMPTY_STYLE_DNA_TRIPLE: StyleDnaTripleSnapshot = {
  general: "",
  character: "",
  environment: ""
};

/** Image model ids excluded from Style DNA test generation (still available elsewhere in the app). */
const STYLE_DNA_EXCLUDED_IMAGE_MODEL_IDS = new Set(["runwayml"]);

export function styleDnaTestImageModels(models: readonly ImageModelOption[]): readonly ImageModelOption[] {
  return models.filter((m) => !STYLE_DNA_EXCLUDED_IMAGE_MODEL_IDS.has(m.image_model));
}

export function styleDnaValueForKey(style: StyleDnaFieldSource, key: StyleDnaDocumentKey): string {
  // oxlint-disable-next-line default-case
  switch (key) {
    case "general":
      return style.general_style_dna ?? "";
    case "character":
      return style.character_style_dna ?? "";
    case "environment":
      return style.environment_style_dna ?? "";
  }
}

export function trimStyleDnaField(value: string | undefined | null): string {
  return (value ?? "").trim();
}

export function trimmedStyleDnaTriple(style: DefaultStyleResponse): { general: string; character: string; environment: string } {
  return {
    general: trimStyleDnaField(style.general_style_dna),
    character: trimStyleDnaField(style.character_style_dna),
    environment: trimStyleDnaField(style.environment_style_dna)
  };
}

const ALL_STYLE_DNA_DOCUMENT_KEYS: readonly StyleDnaDocumentKey[] = ["general", "character", "environment"];

/** When `keys` is omitted or undefined, compares general, character, and environment (full triple). */
export function isStyleDnaTripleDirty(value: StyleDnaTriple, baseline: StyleDnaTriple, keys?: readonly StyleDnaDocumentKey[]): boolean {
  const compareKeys = keys ?? ALL_STYLE_DNA_DOCUMENT_KEYS;
  return compareKeys.some((k) => trimStyleDnaField(value[k]) !== trimStyleDnaField(baseline[k]));
}

export enum StyleDnaType {
  Character = "Character",
  Location = "Location"
}

export function combineStyleDnaTriple(value: StyleDnaTriple, type: StyleDnaType): string {
  const general = trimStyleDnaField(value.general);
  const character = trimStyleDnaField(value.character);
  const environment = trimStyleDnaField(value.environment);

  // oxlint-disable-next-line default-case
  switch (type) {
    case StyleDnaType.Character:
      return [`General Style DNA:\n${general}`, `Character Style DNA:\n${character}`].join("\n\n");
    case StyleDnaType.Location:
      return [`General Style DNA:\n${general}`, `Location Style DNA:\n${environment}`].join("\n\n");
  }
}

/**
 * Appends image injection rules for the given {@link StyleDnaType}.
 * When {@link hasStyleDna} is true, rules are prefixed with `INJECTION RULES:` (style-DNA test flow).
 * When false, only the bullet lines are appended (no header).
 */
export function appendInjectionRules(prompt: string, type: StyleDnaType, hasStyleDna: boolean): string {
  let injectionRules: string;
  // oxlint-disable-next-line default-case
  switch (type) {
    case StyleDnaType.Character:
      injectionRules = hasStyleDna
        ? [
            "INJECTION RULES:",
            "- Render a single character, full body, front-facing.",
            "- Background must be a pure white seamless backdrop.",
            "- Reflect the context through the level of realism, rendering style, lighting, and mood."
          ].join("\n")
        : [
            "- Render a single character, full body, front-facing.",
            "- Background must be a pure white seamless backdrop.",
            "- Reflect the context through the level of realism, rendering style, lighting, and mood."
          ].join("\n");
      break;
    case StyleDnaType.Location:
      injectionRules = hasStyleDna
        ? [
            "INJECTION RULES:",
            "- The image must NEVER contain any characters or humanoid figures unless explicitly stated.",
            "- Reflect the context through the level of realism, rendering style, lighting, and mood."
          ].join("\n")
        : [
            "- The image must NEVER contain any characters or humanoid figures unless explicitly stated.",
            "- Reflect the context through the level of realism, rendering style, lighting, and mood."
          ].join("\n");
      break;
  }

  return `${prompt}\n\n${injectionRules}`;
}

export function styleDnaGenerationAuditFields(email: string | undefined | null): Pick<StyleDnaGeneration, "created_date" | "created_by"> {
  const createdBy = email?.trim();

  return {
    created_date: new Date().toISOString(),
    ...(createdBy ? { created_by: createdBy } : {})
  };
}

/** Rows of one style test run are keyed by `created_date`. */
export function styleDnaGenerationsWithRunUpsert(
  existing: readonly StyleDnaGeneration[],
  run: StyleDnaGeneration
): StyleDnaGeneration[] {
  const runKey = trimStyleDnaField(run.created_date);
  const index = runKey.length === 0 ? -1 : existing.findIndex((gen) => trimStyleDnaField(gen.created_date) === runKey);
  if (index === -1) {
    return [run, ...existing];
  }

  return existing.map((gen, i) => (i === index ? run : gen));
}

/** `transform` returning null drops the row; a missing row is seeded from `template`. */
export function styleDnaGenerationsWithRunTransform(
  existing: readonly StyleDnaGeneration[],
  runKey: string,
  transform: (gen: StyleDnaGeneration) => StyleDnaGeneration | null,
  template?: StyleDnaGeneration
): StyleDnaGeneration[] | null {
  const key = trimStyleDnaField(runKey);
  const index = key.length === 0 ? -1 : existing.findIndex((gen) => trimStyleDnaField(gen.created_date) === key);
  if (index === -1) {
    if (!template) {
      return null;
    }

    const seeded = transform(template);
    return seeded === null ? null : [seeded, ...existing];
  }

  const next = transform(existing[index]!);
  if (next === null) {
    return existing.filter((_, i) => i !== index);
  }

  return existing.map((gen, i) => (i === index ? next : gen));
}

export function styleDnaPendingGenerations(gen: StyleDnaGeneration | null | undefined): readonly StyleDnaPendingGeneration[] {
  const raw = gen?.pending_generations;
  if (!Array.isArray(raw)) {
    return [];
  }

  return raw.filter(
    (entry): entry is StyleDnaPendingGeneration =>
      typeof entry?.transaction_id === "string" &&
      entry.transaction_id.length > 0 &&
      typeof entry.image_model === "string" &&
      entry.image_model.length > 0
  );
}

export function unfinishedStyleDnaRuns(
  style: DefaultStyleResponse | null | undefined
): ReadonlyArray<{ readonly runKey: string; readonly pending: readonly StyleDnaPendingGeneration[] }> {
  return (style?.style_dna_generations ?? [])
    .map((gen) => ({ runKey: trimStyleDnaField(gen.created_date), pending: styleDnaPendingGenerations(gen) }))
    .filter((run) => run.runKey.length > 0 && run.pending.length > 0);
}

export function styleDnaGenerationWithAttachedImage(
  gen: StyleDnaGeneration,
  job: { readonly transactionId: string; readonly imageId: string; readonly imageModel: string }
): StyleDnaGeneration {
  const pending = styleDnaPendingGenerations(gen).filter((entry) => entry.transaction_id !== job.transactionId);
  const images = parseGenerationImageIds(gen.images);
  if (images.includes(job.imageId)) {
    return { ...gen, pending_generations: pending };
  }

  return {
    ...gen,
    images: [...images, job.imageId],
    image_models: [...(gen.image_models ?? []), job.imageModel],
    pending_generations: pending
  };
}

export function styleDnaGenerationWithoutPendingJob(gen: StyleDnaGeneration, transactionId: string): StyleDnaGeneration {
  return {
    ...gen,
    pending_generations: styleDnaPendingGenerations(gen).filter((entry) => entry.transaction_id !== transactionId)
  };
}

export function styleDnaCompletedGenerations(gens: readonly StyleDnaGeneration[]): readonly StyleDnaGeneration[] {
  return gens.filter((gen) => parseGenerationImageIds(gen.images).length > 0);
}

const STYLE_TEST_DISPLAY_NAME_PREFIX = "Style test ";
const STYLE_TEST_RUN_ID_SEPARATOR = " · ";

export function styleTestDisplayName(index: number, runId: string): string {
  return `${STYLE_TEST_DISPLAY_NAME_PREFIX}${index + 1}${STYLE_TEST_RUN_ID_SEPARATOR}${runId}`;
}

export function styleTestRunIdFromDisplayName(displayName: string | null | undefined): string {
  if (typeof displayName !== "string" || !displayName.startsWith(STYLE_TEST_DISPLAY_NAME_PREFIX)) {
    return "";
  }

  const separatorAt = displayName.lastIndexOf(STYLE_TEST_RUN_ID_SEPARATOR);
  return separatorAt === -1 ? "" : displayName.slice(separatorAt + STYLE_TEST_RUN_ID_SEPARATOR.length).trim();
}

export interface CompletedStyleTestImage {
  readonly runId: string;
  readonly transactionId: string;
  readonly imageId: string;
  readonly imageModel: string;
}

/**
 * Attaches finished style test images to their run's row. Recovers images whose run stopped being
 * driven — a closed tab, a reload — and returns null when every image is already attached.
 */
export function styleDnaGenerationsWithRecoveredImages(
  existing: readonly StyleDnaGeneration[],
  completed: readonly CompletedStyleTestImage[]
): StyleDnaGeneration[] | null {
  let rows = [...existing];
  let changed = false;

  for (const image of completed) {
    if (image.runId.length === 0) {
      continue;
    }

    const index = rows.findIndex((gen) => trimStyleDnaField(gen.run_id) === image.runId);
    if (index === -1) {
      continue;
    }

    const row = rows[index]!;
    const attachedAlready = parseGenerationImageIds(row.images).includes(image.imageId);
    const stillPending = styleDnaPendingGenerations(row).some((entry) => entry.transaction_id === image.transactionId);
    if (attachedAlready && !stillPending) {
      continue;
    }

    const next = styleDnaGenerationWithAttachedImage(row, {
      transactionId: image.transactionId,
      imageId: image.imageId,
      imageModel: image.imageModel
    });

    rows = rows.map((gen, i) => (i === index ? next : gen));
    changed = true;
  }

  return changed ? rows : null;
}

export function styleDnaPanelResetBaseline(style: DefaultStyleResponse | null | undefined): {
  general: string;
  character: string;
  environment: string;
} {
  if (!style) {
    return { general: "", character: "", environment: "" };
  }

  const selected = style.style_dna_generations?.find((g) => g.selected);

  if (selected) {
    return {
      general: trimStyleDnaField(selected.general_style_dna),
      character: trimStyleDnaField(selected.character_style_dna),
      environment: trimStyleDnaField(selected.environment_style_dna)
    };
  }

  return trimmedStyleDnaTriple(style);
}

export function styleDnaDraftBaselineKey(projectId: string | null, styleRecordId: string | null): string {
  return `${projectId ?? ""}:${styleRecordId ?? ""}`;
}

export const STYLE_DNA_TEST_DESCRIPTION_MAX_CHARS = 1000;

export function styleDnaGenerationsHaveSelected(generations: readonly StyleDnaGeneration[] | undefined): boolean {
  return generations?.some((g) => g.selected) ?? false;
}

export function trimmedSelectedGenerationDnaTriple(style: DefaultStyleResponse | null | undefined): {
  general: string;
  character: string;
  environment: string;
} | null {
  const g = style?.style_dna_generations?.find((x) => x.selected);
  if (!g) {
    return null;
  }

  return {
    general: trimStyleDnaField(g.general_style_dna),
    character: trimStyleDnaField(g.character_style_dna),
    environment: trimStyleDnaField(g.environment_style_dna)
  };
}

/** Character / Location panels only: DNA baseline from the selected test generation (`style_dna_generations[].selected`). Otherwise empty triple — not the extracted root style fields. */
export function entityPanelSelectedStyleDnaTripleOrEmpty(style: DefaultStyleResponse | null | undefined): StyleDnaTripleSnapshot {
  return trimmedSelectedGenerationDnaTriple(style) ?? EMPTY_STYLE_DNA_TRIPLE;
}


/** Style summary for image generation: confirmed generation first, then extracted root field. */
export function styleSummaryFromStyleDocument(style: DefaultStyleResponse | null | undefined): string {
  const selected = style?.style_dna_generations?.find((g) => g.selected);
  if (selected) {
    const fromSelected = trimStyleDnaField(selected.style_summary);
    if (fromSelected.length > 0) {
      return fromSelected;
    }
  }

  return trimStyleDnaField(style?.style_summary);
}

/** Baseline Style Summary text for the Style DNA panel reset / view modes. */
export function styleSummaryPanelBaseline(
  style: DefaultStyleResponse | null | undefined,
  panelPreviewMode: StyleDnaPanelPreviewMode = null
): string {
  if (!style) {
    return "";
  }

  if (panelPreviewMode === "extracted") {
    return trimStyleDnaField(style.style_summary);
  }

  if (panelPreviewMode === "confirmed") {
    const selected = style.style_dna_generations?.find((g) => g.selected);
    const fromSelected = trimStyleDnaField(selected?.style_summary);
    return fromSelected.length > 0 ? fromSelected : trimStyleDnaField(style.style_summary);
  }

  const selected = style.style_dna_generations?.find((g) => g.selected);
  if (selected) {
    const fromSelected = trimStyleDnaField(selected.style_summary);
    if (fromSelected.length > 0) {
      return fromSelected;
    }
  }

  return trimStyleDnaField(style.style_summary);
}

export function prefillPayloadFromGeneration(gen: StyleDnaGeneration, allImageModelIds: readonly string[]): StyleDnaPanelDraftPrefillPayload {
  const allowed = new Set(allImageModelIds);
  const fromGen =
    gen.image_models !== undefined && Array.isArray(gen.image_models) && gen.image_models.length > 0
      ? gen.image_models.filter((x): x is string => typeof x === "string" && allowed.has(x))
      : [];
  const imageModels = fromGen.length > 0 ? fromGen : [...allImageModelIds];

  return {
    general: gen.general_style_dna ?? "",
    character: gen.character_style_dna ?? "",
    environment: gen.environment_style_dna ?? "",
    prompt: (gen.prompt ?? "").slice(0, STYLE_DNA_TEST_DESCRIPTION_MAX_CHARS),
    imageModels
  };
}

export function resolveStyleDnaPanelImageModelSelection(style: DefaultStyleResponse, metadataModels: readonly ImageModelOption[]): string[] {
  const allowedIds = metadataModels.map((m) => m.image_model);
  const allowed = new Set(allowedIds);
  const gens = styleDnaCompletedGenerations(style.style_dna_generations ?? []);
  const selectedGen = gens.find((g) => g.selected) ?? gens[0];
  const raw = selectedGen?.image_models;
  if (Array.isArray(raw) && raw.length > 0) {
    const filtered = raw.filter((x): x is string => typeof x === "string" && allowed.has(x));
    if (filtered.length > 0) {
      return filtered;
    }
  }

  return [...allowedIds];
}

export function parseReferenceImageIds(raw: readonly unknown[] | undefined | null): readonly string[] {
  if (!raw || raw.length === 0) {
    return [];
  }

  return raw
    .map((item) => {
      if (typeof item === "string") {
        return item;
      }

      if (item && typeof item === "object" && "id" in item && typeof (item as { id: unknown }).id === "string") {
        return (item as { id: string }).id;
      }

      return null;
    })
    .filter((id): id is string => typeof id === "string" && id.length > 0);
}

export function parseGenerationImageIds(images: readonly unknown[] | undefined | null): readonly string[] {
  if (!images || images.length === 0) {
    return [];
  }

  return images
    .map((img) => (typeof img === "string" ? img : ((img as { id?: string })?.id ?? String(img))))
    .filter((id): id is string => typeof id === "string" && id.length > 0);
}

export function selectedStyleDnaGenerationPrimaryImageId(style: DefaultStyleResponse | null | undefined): string | null {
  if (!style) {
    return null;
  }

  const g = style.style_dna_generations?.find((gen) => gen.selected);
  if (!g) {
    return null;
  }

  return parseGenerationImageIds(g.images)[0] ?? null;
}

export function withGenerationImagePrimary(gen: StyleDnaGeneration, primaryImageId: string): StyleDnaGeneration {
  const imageIds = [...parseGenerationImageIds(gen.images)];
  if (imageIds.length === 0) {
    return { ...gen, images: [primaryImageId] };
  }

  const idx = imageIds.indexOf(primaryImageId);
  if (idx === -1) {
    return { ...gen, images: [primaryImageId, ...imageIds] };
  }

  if (idx === 0) {
    return { ...gen, images: imageIds };
  }

  const reordered = [primaryImageId, ...imageIds.filter((id) => id !== primaryImageId)];

  const models = gen.image_models;
  let image_models: readonly string[] | undefined;
  if (Array.isArray(models) && models.length === imageIds.length) {
    const m = models[idx]!;
    image_models = [m, ...models.filter((_, i) => i !== idx)] as string[];
  } else {
    image_models = models;
  }

  return { ...gen, images: reordered, image_models };
}

export function styleDocumentContainsImageId(style: DefaultStyleResponse, imageId: string): boolean {
  if (imageId.length === 0) {
    return false;
  }

  if (parseReferenceImageIds(style.reference_images).includes(imageId)) {
    return true;
  }

  for (const g of style.style_dna_generations ?? []) {
    if (parseGenerationImageIds(g.images).includes(imageId)) {
      return true;
    }
  }

  return false;
}

const IMAGE_MODEL_FALLBACK = "—";

export function styleDnaTestResultHeaderModelLine(
  style: DefaultStyleResponse,
  imageModels: ReadonlyArray<{ image_model: string; display_name: string }>,
  sessionImageId: string | null
): string {
  const byId = new Map(imageModels.map((m) => [m.image_model, m.display_name] as const));
  const genList = styleDnaCompletedGenerations(style.style_dna_generations ?? []);
  if (genList.length === 0) {
    return IMAGE_MODEL_FALLBACK;
  }

  const modelLabelAt = (g: StyleDnaGeneration, imageIndex: number): string => {
    const mid = g.image_models?.[imageIndex];
    if (typeof mid === "string" && mid.length > 0) {
      return byId.get(mid) ?? mid;
    }

    return imageModels[imageIndex]?.display_name ?? IMAGE_MODEL_FALLBACK;
  };

  const selected = genList.find((d) => d.selected);
  if (selected) {
    return modelLabelAt(selected, 0);
  }

  if (sessionImageId) {
    for (const g of genList) {
      const imageIds = [...parseGenerationImageIds(g.images)];
      const ii = imageIds.indexOf(sessionImageId);
      if (ii !== -1) {
        return modelLabelAt(g, ii);
      }
    }
  }

  return modelLabelAt(genList[0]!, 0);
}

export function formatStyleDateTime(iso: string | undefined): string {
  if (!iso?.trim()) {
    return "—";
  }

  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) {
    return iso;
  }

  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit"
  });
}
